/**
 * Окно учётной записи, ждущей удаления.
 *
 * Появляется, когда человек вошёл в аккаунт, удаление которого уже назначено.
 * Закрыть его мимо кнопок нельзя: сервер с таким токеном всё равно почти
 * ничего не разрешит, и сначала нужно решить — отменить удаление или уйти.
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { errorMessage } from "../lib/authForm";
import { formatDateTime } from "../lib/format";
import { serverNow } from "../lib/serverClock";
import { useAuthStore } from "../stores/authStore";
import { Dialog } from "./Dialog";

/** «23 ч 41 мин» до удаления; пересчитывается раз в минуту. */
function useRemaining(until: string | undefined): { hours: number; minutes: number } {
  const [now, setNow] = useState(serverNow);
  useEffect(() => {
    const timer = window.setInterval(() => setNow(serverNow()), 30_000);
    return () => window.clearInterval(timer);
  }, []);
  const left = Math.max(0, new Date(until ?? 0).getTime() - now);
  return { hours: Math.floor(left / 3_600_000), minutes: Math.floor((left % 3_600_000) / 60_000) };
}

export function DeletionPendingDialog() {
  const user = useAuthStore((state) => state.user);
  const scheduled = user?.deletionScheduledAt;
  if (!scheduled) return null;
  return <PendingDialog scheduled={scheduled} />;
}

function PendingDialog({ scheduled }: { scheduled: string }) {
  const { t } = useTranslation();
  const logout = useAuthStore((state) => state.logout);
  const cancelDeletion = useAuthStore((state) => state.cancelDeletion);
  const remaining = useRemaining(scheduled);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cancel = async () => {
    setError(null);
    setBusy(true);
    try {
      await cancelDeletion();
    } catch (failure) {
      setError(errorMessage(failure));
      setBusy(false);
    }
  };

  return (
    <Dialog title={t("deletion.pendingTitle")} onClose={() => undefined} dismissible={false}>
      <div className="space-y-4 text-sm text-ink-300">
        <p className="text-ink-100">
          {t("deletion.pendingText", {
            date: formatDateTime(scheduled),
            hours: remaining.hours,
            minutes: remaining.minutes,
          })}
        </p>
        <p>{t("deletion.pendingHint")}</p>
        {error && <p className="text-xs text-rust">{error}</p>}
        <div className="flex flex-col gap-2 sm:flex-row">
          <button type="button" className="btn-primary flex-1" disabled={busy} onClick={() => void cancel()}>
            {t("deletion.cancelButton")}
          </button>
          <button type="button" className="btn-secondary flex-1" disabled={busy} onClick={() => void logout()}>
            {t("deletion.logoutButton")}
          </button>
        </div>
      </div>
    </Dialog>
  );
}
